import React, { useEffect, useState } from 'react'
import { noop } from 'lodash'
import { SvgColumn, SvgDrag } from '../assets/images'
import CustomButton from './CustomButton'
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd'
import { ColumnListType, NAMESPACE } from '../utils'

interface DraggableMultiSelectProps {
  currentCol: ColumnListType[]
  colSetter: React.Dispatch<React.SetStateAction<ColumnListType[]>>
  colList: ColumnListType[]
}

const DraggableMultiSelect: React.FC<DraggableMultiSelectProps> = ({
  currentCol,
  colSetter,
  colList,
}) => {
  const [moreColumns, setMoreColumns] = useState<boolean>(false)
  const [checkedCol, setCheckedCol] = useState<ColumnListType[]>([])

  useEffect(() => {
    setCheckedCol(currentCol)
  }, [currentCol])

  const handleCheckedPopulate = (column: ColumnListType) => {
    if (column.isMandatory) return
    setCheckedCol((prevCheckedCol) => {
      if (prevCheckedCol.some((col) => col.label === column.label)) {
        return prevCheckedCol.filter((col) => col.label !== column.label)
      } else {
        const lastTwoCols = prevCheckedCol.slice(-2)
        const filteredColsWithoutLastTwo = prevCheckedCol.slice(0, -2)

        return [...filteredColsWithoutLastTwo, column, ...lastTwoCols]
      }
    })
  }

  const handleAdd = () => {
    const newColumns = checkedCol.filter(
      (col) => !currentCol.some((current) => current.label === col.label)
    )
    const filteredColumns = currentCol.filter((col) =>
      checkedCol.some((checked) => checked.label === col.label)
    )
    const lastTwoCols = filteredColumns.slice(-2)
    const filteredColsWithoutLastTwo = filteredColumns.slice(0, -2)

    colSetter([...filteredColsWithoutLastTwo, ...newColumns, ...lastTwoCols])
    setMoreColumns(false)
  }

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination) return

    const movedColumn = checkedCol.find(
      (col) => col.label === result.draggableId
    )
    if (!movedColumn) {
      console.error('Moved column not found')
      return
    }
    const reorderedColumns = Array.from(checkedCol)
    const currentIndex = reorderedColumns.findIndex(
      (col) => col.label === result.draggableId
    )
    reorderedColumns.splice(currentIndex, 1)
    reorderedColumns.splice(result.destination.index + 1, 0, movedColumn)

    setCheckedCol(reorderedColumns)
    colSetter(reorderedColumns)
  }

  return (
    <div className="relative">
      <button onClick={() => setMoreColumns((prev) => !prev)}>
        <span>
          <SvgColumn />
        </span>
      </button>
      {moreColumns && (
        <div className="flex flex-col absolute top-10 right-[0rem] bg-accent-0 gap-4 z-50 border-boxColour border rounded-lg">
          <div className="h-[15rem] overflow-y-auto">
            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable droppableId="column-list">
                {(provided) => (
                  <ul
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{ listStyle: 'none', padding: 0 }}
                  >
                    {colList.map((col, index) => (
                      <Draggable
                        key={col.label}
                        draggableId={col.label}
                        index={index}
                      >
                        {(provided) => (
                          <li
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`flex items-center p-1 pl-4 bg-accent-0 justify-between rounded-lg ${
                              col.isMandatory
                                ? ' text-accent-400 cursor-not-allowed opacity-80'
                                : ''
                            }`}
                            style={{
                              ...provided.draggableProps.style,
                            }}
                          >
                            <div
                              onClick={() => handleCheckedPopulate(col)}
                              className="flex items-center"
                            >
                              <input
                                type="checkbox"
                                onChange={noop}
                                checked={checkedCol.some(
                                  (checked) => checked.label === col.label
                                )}
                                className={`mr-2${
                                  col.isMandatory
                                    ? ' cursor-not-allowed opacity-60'
                                    : ''
                                }`}
                              />
                              <span className="font-normal">{col.label}</span>
                            </div>
                            {/* Drag Handle */}
                            <div
                              {...provided.dragHandleProps}
                              style={{
                                cursor: col.isMandatory
                                  ? 'not-allowed'
                                  : 'grab',
                              }}
                              className={`p-2 mr-1${
                                col.isMandatory ? ' opacity-80' : ''
                              }`}
                            >
                              <SvgDrag /> {/* Drag icon */}
                            </div>
                          </li>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          <div className="btngrp flex gap-3 p-4">
            <CustomButton
              variant="secondary"
              size="small"
              width="8.2rem"
              onClick={() => setMoreColumns(false)}
            >
              Cancel
            </CustomButton>
            <CustomButton
              isFilled={true}
              size="small"
              width="8.2rem"
              type="submit"
              onClick={handleAdd}
            >
              {NAMESPACE?.ADD}
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  )
}

export default DraggableMultiSelect
